import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import NotificationsNoneOutlined from "@mui/icons-material/NotificationsNoneOutlined";
import KeyboardArrowDownOutlined from "@mui/icons-material/KeyboardArrowDownOutlined";
import MenuOutlined from "@mui/icons-material/MenuOutlined";

import HeaderDrawer from "../HeadbarDrawer/component";
import { logout } from "../../slices/signIn.slice";
import { clearError } from "../../slices/Employee/editEmployeeProfile.slice";
import { cn } from "../ds/cn";
import { useShell } from "./ShellContext";
import styles from "./TopBar.module.scss";

/**
 * Shared shell TopBar. Preserves the existing header behavior — Edit Profile /
 * Change Password (via the existing HeaderDrawer) and Logout — in the new design.
 */
export default function TopBar({ user }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    collapsed,
    toggleCollapsed,
    isMobile,
    mobileNavOpen,
    toggleMobileNav,
  } = useShell();

  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editProfileDrawer, setEditProfileDrawer] = useState(false);
  const [changePasswordDrawer, setChangePasswordDrawer] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    dispatch(clearError());
  }, [drawerVisible, dispatch]);

  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current) {
      setMenuPos(null);
      return undefined;
    }
    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      const inTrigger = menuRef.current && menuRef.current.contains(e.target);
      const inDropdown =
        dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inTrigger && !inDropdown) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const items = itemRefs.current.filter(Boolean);
      if (!items.length) return;
      e.preventDefault();
      const idx = items.indexOf(document.activeElement);
      let next = idx;
      if (e.key === "ArrowDown") next = idx < 0 ? 0 : (idx + 1) % items.length;
      else next = idx <= 0 ? items.length - 1 : idx - 1;
      items[next].focus();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const openDrawer = (type) => {
    setMenuOpen(false);
    setDrawerVisible(true);
    setEditProfileDrawer(type === "Edit Profile");
    setChangePasswordDrawer(type !== "Edit Profile");
  };

  const handleChangePasswordOnClick = () => {
    setEditProfileDrawer(false);
    setChangePasswordDrawer(true);
  };

  const handleLogout = () => {
    dispatch(logout());
    localStorage.clear();
    history.replace("/");
  };

  const onNavToggle = () => {
    if (isMobile) toggleMobileNav();
    else toggleCollapsed();
  };

  const navLabel = isMobile
    ? mobileNavOpen
      ? "Close navigation"
      : "Open navigation"
    : collapsed
      ? "Expand sidebar"
      : "Collapse sidebar";

  const displayName = user?.user_name || user?.name || "User";
  const firstName = displayName.split(" ")[0];
  const email = user?.user_email || user?.email || "";
  const organization = user?.organization || "";

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.left}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onNavToggle}
            aria-label={navLabel}
            aria-expanded={isMobile ? mobileNavOpen : !collapsed}
            title={navLabel}
          >
            <MenuOutlined fontSize="small" />
          </button>
        </div>

        <div className={styles.right}>
          <button type="button" className={styles.iconBtn} aria-label="Notifications">
            <NotificationsNoneOutlined fontSize="small" />
          </button>

          <div className={styles.userMenu} ref={menuRef}>
            <button
              type="button"
              ref={triggerRef}
              className={styles.userTrigger}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className={styles.userName}>{firstName}</span>
              <KeyboardArrowDownOutlined
                fontSize="small"
                className={cn(styles.chevron, menuOpen && styles.chevronOpen)}
              />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && menuPos
        ? ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className={styles.dropdown}
              role="menu"
              style={{ top: menuPos.top, right: menuPos.right }}
            >
              <div className={styles.dropdownHead}>
                <div className={styles.ddName}>{displayName}</div>
                {organization ? <div className={styles.ddMeta}>{organization}</div> : null}
                {email ? <div className={styles.ddMeta}>{email}</div> : null}
              </div>
              <button
                type="button"
                role="menuitem"
                ref={(el) => {
                  itemRefs.current[0] = el;
                }}
                className={styles.ddItem}
                onClick={() => openDrawer("Edit Profile")}
              >
                Edit Profile
              </button>
              <button
                type="button"
                role="menuitem"
                ref={(el) => {
                  itemRefs.current[1] = el;
                }}
                className={styles.ddItem}
                onClick={() => openDrawer("Change Password")}
              >
                Change Password
              </button>
              <button
                type="button"
                role="menuitem"
                ref={(el) => {
                  itemRefs.current[2] = el;
                }}
                className={cn(styles.ddItem, styles.ddDanger)}
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>,
            document.body
          )
        : null}

      <HeaderDrawer
        drawerVisible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        editProfileDrawer={editProfileDrawer}
        changePasswordDrawer={changePasswordDrawer}
        setEditProfileDrawer={setEditProfileDrawer}
        handleChangePasswordOnClick={handleChangePasswordOnClick}
      />
    </>
  );
}

TopBar.propTypes = {
  user: PropTypes.object,
};
